/**
 * Correction partielle d'une fiche client (super-admin).
 *
 * Deux raisons d'exister à côté du formulaire client complet :
 *  - il charge la fiche détaillée avant d'éditer. La liste, elle, est alimentée par la recherche,
 *    qui renvoie une réponse « simple » : renvoyer cet objet en PUT écraserait les champs qu'elle ne
 *    porte pas ;
 *  - il n'envoie que ce qui a changé, donc il peut corriger un nom sans rien dire du reste.
 *
 * Il couvre exactement trois champs : le nom, le NIP et l'identifiant Odoo. C'est ce qu'il faut
 * pour rattacher une fiche legacy et la rendre identifiable ; tout le reste — notes, interrupteurs,
 * dernière intervention, adresse, contact, secteur, modèle de rapport — appartient au formulaire
 * client complet, qui porte déjà leurs contrôles.
 *
 * L'identifiant Odoo est ici lui aussi, mais il ne passe PAS par le corps du PATCH : il a son
 * propre verbe super-admin, qui garantit l'unicité et purge les créances stockées. Il est dans ce
 * tiroir parce que ses deux autres portes d'écriture traversent le formulaire client complet, dont
 * la validation exige email, NIF, adresse et secteur — précisément ce qui bloque la correction
 * d'une fiche legacy incomplète.
 */
import { useCallback, useEffect, useState } from "react";
import { Button, Input, Typography } from "@material-tailwind/react";
import toast from "react-hot-toast";
import { t } from "i18next";
import Loading from "../../../../components/loading/Loading";
import { useDrawerLayoutDispatch } from "../../../../components/drawer-layout/DrawerLayout";
import { showApiErrorToast } from "../../../../services/api/error";
import { isOdooConnected, saveExternalId } from "../../../../services/odoo/odoo";
import {
  buildCustomerPatch,
  getCustomerForPatch,
  patchCustomer,
  CustomerPatchDraft
} from "../../../../services/customer/customerPatch";

const TEXT_FIELDS: Array<keyof CustomerPatchDraft> = ["name", "nip"];

/** `externalId` est typé `number | string` : `0` est un identifiant valide, pas une absence. */
const asExternalIdText = (value?: number | string | null): string =>
  value === undefined || value === null ? "" : String(value).trim();

const toDraft = (customer: EasyTech.Customer): CustomerPatchDraft => ({
  name: customer.name ?? "",
  nip: customer.nip ?? ""
});

export default function AdminCustomerPatchDrawer({
  customerId,
  onPatched
}: {
  customerId: string,
  onPatched?: () => void
}) {
  const dispatch = useDrawerLayoutDispatch();
  const [baseline, setBaseline] = useState<EasyTech.Customer | null>(null);
  const [draft, setDraft] = useState<CustomerPatchDraft | null>(null);
  const [externalId, setExternalId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Le realm décide : sans Odoo en face, l'identifiant n'a rien à éditer.
  const canEditExternalId = isOdooConnected();

  useEffect(() => {
    let cancelled = false;
    getCustomerForPatch(customerId)
      .then((customer) => {
        if (cancelled) return;
        setBaseline(customer);
        setDraft(toDraft(customer));
        setExternalId(asExternalIdText(customer.externalId));
      })
      .catch((error) => {
        if (cancelled) return;
        showApiErrorToast(error, { fallbackKey: "admin:customers.patch.loadError" });
      });
    return () => { cancelled = true; };
  }, [customerId]);

  const change = useCallback((field: keyof CustomerPatchDraft, value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  }, []);

  const submit = async () => {
    if (!baseline || !draft) return;

    if (draft.name.trim() === "") {
      toast.error(t("admin:customers.patch.nameRequired"));
      return;
    }

    const body = buildCustomerPatch(baseline, draft);

    // L'identifiant Odoo vit hors du corps du PATCH, donc « rien à enregistrer » doit le consulter :
    // une correction qui ne touche que lui laisse un corps vide sans être pour autant un non-événement.
    const odooTouched = canEditExternalId
      && externalId.trim() !== asExternalIdText(baseline.externalId);

    if (Object.keys(body).length === 0 && !odooTouched) {
      toast.error(t("admin:customers.patch.nothingToSave"));
      return;
    }

    setSubmitting(true);

    // L'identifiant Odoo part en premier : un 409 sur un identifiant déjà pris laisse la fiche
    // entièrement intacte et le tiroir ouvert sur la saisie. Le PATCH ne suit que s'il a abouti.
    if (odooTouched) {
      try {
        await saveExternalId("customers", baseline.id, baseline.externalId, externalId);
      } catch (error) {
        showApiErrorToast(error, { fallbackKey: "client:odoo:externalId:updateError" });
        setSubmitting(false);
        return;
      }
    }

    try {
      if (Object.keys(body).length > 0) await patchCustomer(baseline.id, body);
      toast.success(t("admin:customers.patch.success"));
      onPatched?.();
      dispatch({ type: "close" });
    } catch (error) {
      showApiErrorToast(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!draft) return <Loading />;

  return (
    <div className="flex flex-col gap-4 p-1">
      <Typography variant="small" className="text-blue-gray-700">
        {t("admin:customers.patch.intro")}
      </Typography>

      {TEXT_FIELDS.map((field) => (
        <Input
          key={field}
          crossOrigin=""
          label={t(`admin:customers.patch.${field}`)}
          value={draft[field]}
          onChange={(e) => change(field, e.target.value)}
        />
      ))}

      {canEditExternalId && (
        <Input
          crossOrigin=""
          label={t("admin:customers.patch.externalId")}
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
        />
      )}

      <div className="mt-2 flex justify-end gap-2">
        <Button size="sm" variant="outlined" onClick={() => dispatch({ type: "close" })} disabled={submitting}>
          {t("admin:customers.patch.cancel")}
        </Button>
        <Button size="sm" color="blue" onClick={submit} disabled={submitting}>
          {t("admin:customers.patch.submit")}
        </Button>
      </div>
    </div>
  );
}
